import { computeEligiblePopups } from '~helpers/PopupStorage/computeEligiblePopups';
import { PopupStorage } from '~helpers/PopupStorage/PopupStorage';
import {
  MappedPopupAction,
  StrapiPopupItem,
} from '~helpers/strapi/mapPopupActionProps';
import { ActivePopup } from '~types/PopupStorage.types';

const COOLDOWN_TICK_BUFFER_MS = 100;
const COOLDOWN_END_BUFFER_MS = 200;

export type PopupSchedulerCallbacks = {
  onShow: (popup: ActivePopup) => void;
  onClose: () => void;
};

export class PopupScheduler {
  private items: StrapiPopupItem[] = [];
  private cooldownSeconds = 3600;
  private eligible: MappedPopupAction[] = [];
  private activeId: string | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private cooldownTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly callbacks: PopupSchedulerCallbacks) {}

  setCooldownSeconds(seconds: number) {
    this.cooldownSeconds = seconds;
  }

  setItems(items: StrapiPopupItem[]) {
    this.items = items;
  }

  getEligible(): MappedPopupAction[] {
    return this.eligible;
  }

  start() {
    if (this.items.length) {
      const activeIds = this.items
        .map((item) => item.popup_action?.documentId)
        .filter(Boolean) as string[];

      PopupStorage.cleanupStaleEntries(activeIds);
    }

    if (PopupStorage.isCooldownActive()) {
      this.scheduleCooldownRetry();

      return;
    }

    this.showNext();
  }

  showNext() {
    this.clearShowTimer();

    this.eligible = computeEligiblePopups(this.items);

    if (this.eligible.length === 0) return;

    const next = this.eligible[0];

    this.showTimer = setTimeout(() => {
      if (PopupStorage.isCooldownActive()) {
        this.scheduleCooldownRetry();

        return;
      }

      PopupStorage.recordShow(next.id, next.maxShows);
      this.activeId = next.id;

      this.callbacks.onShow({
        id: next.id,
        props: next.props,
        allTargetActions: next.allTargetActions,
        maxShows: next.maxShows,
      });
    }, next.delay);
  }

  close() {
    if (this.activeId) {
      PopupStorage.recordClose(this.activeId);
    }

    this.activeId = null;
    this.clearShowTimer();

    PopupStorage.setCooldown(this.cooldownSeconds);

    this.clearCooldownTimer();
    this.cooldownTimer = setTimeout(
      () => this.showNext(),
      this.cooldownSeconds * 1000 + COOLDOWN_END_BUFFER_MS,
    );

    this.callbacks.onClose();
  }

  recordTargetAction(
    popupId: string,
    actionValue: string,
    shouldClose: boolean,
  ): { handled: boolean; allCompleted: boolean } {
    const popup = this.eligible.find((p) => p.id === popupId);

    if (!popup) return { handled: false, allCompleted: false };

    const allCompleted = PopupStorage.recordTargetAction(
      popupId,
      actionValue,
      popup.allTargetActions,
    );

    if (shouldClose) {
      this.close();
    }

    return { handled: true, allCompleted };
  }

  dispose() {
    this.clearShowTimer();

    this.clearCooldownTimer();
  }

  private scheduleCooldownRetry() {
    const remaining = PopupStorage.read().nextPopupShowTime - Date.now();

    this.clearCooldownTimer();
    this.cooldownTimer = setTimeout(
      () => this.showNext(),
      remaining + COOLDOWN_TICK_BUFFER_MS,
    );
  }

  private clearShowTimer() {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearCooldownTimer() {
    if (this.cooldownTimer) {
      clearTimeout(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }
}
